import { Reveal, SectionTag } from "./shared";
import { Boxes, Cog, TrendingUp, ShoppingCart, Receipt } from "lucide-react";

const pillars = [
  { icon: Boxes, name: "Inventory", text: "Excess stock, obsolete items, oversized safety buffers tying up cash unnecessarily." },
  { icon: Cog, name: "Production", text: "Stagnant WIP and inefficient batch sizing that looks good on OEE and hides cash." },
  { icon: TrendingUp, name: "Forecasting", text: "Misaligned supply planning and demand errors that ripple backward into procurement and inventory." },
  { icon: ShoppingCart, name: "Procurement", text: "Suboptimal payment terms and over-ordering for bulk discounts that land as working capital burden." },
  { icon: Receipt, name: "Collections", text: "Invoicing lag and receivables friction that defer cash conversion unnecessarily." },
];

export const Framework = () => (
  <section id="framework" className="py-12 lg:py-16 bg-[#0B1B2B] relative overflow-hidden" data-testid="framework-section">
    <div className="absolute inset-0 dot-grid-dark pointer-events-none" />
    <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
      <SectionTag num="02" label="The Framework" light />
      <div className="max-w-[760px]">
        <Reveal>
          <h2 className="font-display font-extrabold text-white tracking-[-0.02em] leading-[1.05] text-3xl sm:text-4xl lg:text-[44px]">
            We isolate trapped cash across <span className="text-[#7fd6b8]">five structural pillars.</span>
          </h2>
        </Reveal>
      </div>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
        {pillars.map((p, i) => (
          <Reveal key={p.name} delay={0.08 * i} className="h-full">
            <div className="group h-full p-6 rounded-2xl bg-white/[0.05] border border-white/10 hover:bg-white/[0.09] hover:border-[#7fd6b8]/40 transition-all duration-500 hover:-translate-y-1.5" data-testid={`pillar-card-${p.name.toLowerCase()}`}>
              <span className="text-[12px] font-bold tracking-[0.2em] text-[#7fd6b8]/90">0{i + 1}</span>
              <div className="mt-4 w-10 h-10 rounded-lg bg-[#0E5F46]/40 flex items-center justify-center">
                <p.icon className="w-5 h-5 text-[#7fd6b8]" strokeWidth={2} />
              </div>
              <h3 className="mt-4 font-display font-bold text-white text-lg">{p.name}</h3>
              <p className="mt-2 text-white/75 text-[13.5px] leading-relaxed">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-9 max-w-[860px] border-l-2 border-[#7fd6b8] pl-7 py-1">
          <p className="text-white/80 text-base md:text-lg leading-relaxed">
            Each pillar is measured independently <span className="text-white font-semibold">and across their interactions.</span> The
            output is a quantified map of recoverable cash by category — with specific operational drivers and a
            90-day recovery roadmap your ops team can execute <span className="text-[#7fd6b8] font-semibold">without our help.</span>
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
